import cx from 'classnames'
import { memo, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { landingAssetsForLocale } from '../config/landingAssets'
import { PurchaseChannelConfig } from '../types'
import {
  buildPurchaseChannelDisplayStates,
  PurchaseChannelDisplayState,
} from '../utils/purchaseButtonState'

interface PremiumFeature {
  icon: string
  note?: string
  text: string
}

interface StepButton extends PurchaseChannelDisplayState {
  onClick: () => void
}

interface Step {
  stepNumber: number
  badgeText: string
  imageSrc: string
  title: ReactNode
  description: string
  premiumFeatures?: PremiumFeature[]
}

const StepBadge = memo(function StepBadge({ text }: { text: string }) {
  return (
    <div className='flex justify-center'>
      <div className='rounded-full bg-[#F29CB2] px-2 py-0.5 text-[13px] font-semibold text-white'>
        {text}
      </div>
    </div>
  )
})

const StepButtons = memo(function StepButtons({ buttons }: { buttons: StepButton[] }) {
  return (
    <div className='mx-auto flex w-full flex-col gap-3'>
      {buttons.map(button => (
        <a
          id={`step1-${button.channel}-purchase-button`}
          key={button.channel}
          className={cx(
            'block w-full rounded-lg py-[12px] text-center text-base font-semibold text-white',
            {
              'bg-[#202020]': button.channel === 'amazon',
              'bg-[#C70000]': button.channel === 'rakuten',
            },
          )}
          href={button.href}
          target='_blank'
          rel='noreferrer noopener'
          onClick={button.onClick}
        >
          <span className='block'>{button.label}</span>
          {button.subText && (
            <span className='mt-1 block text-xs font-normal tracking-[-0.08px]'>
              {button.subText}
            </span>
          )}
        </a>
      ))}
    </div>
  )
})

const PremiumFeaturesBox = memo(function PremiumFeaturesBox({
  features,
  title,
  layout,
}: {
  features: PremiumFeature[]
  title: string
  layout: 'grid' | 'list'
}) {
  if (layout === 'grid') {
    return (
      <div className='mx-2 mt-6 space-y-6 overflow-hidden rounded-lg bg-[#FEFBE9] py-8'>
        <div className='px-4 text-center text-xl font-bold leading-[140%] tracking-[0.12px]'>
          {title}
        </div>
        <div className='grid grid-cols-3 gap-4 px-4'>
          {features.map((feature, idx) => (
            <div key={idx} className='flex flex-col items-center gap-2 text-center'>
              <img
                src={feature.icon}
                alt=''
                className='mb-2 h-[60px] w-[60px] flex-shrink-0 object-contain'
                loading='lazy'
              />
              <div>
                <p className='text-gray-12 tracking-0 text-[13px] font-bold leading-[160%]'>
                  {feature.text}
                </p>
                {feature.note && (
                  <p className='text-gray-12 text-[13px] font-normal leading-[140%]'>
                    {feature.note}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className='mx-2 mt-6 overflow-hidden rounded-lg'>
      <div className='bg-[#60B3D7] px-4 py-2 text-center font-semibold text-white'>{title}</div>
      <div className='space-y-4 bg-[#F5F5F5] p-4 pl-8'>
        {features.map((feature, idx) => (
          <div key={idx} className='flex items-center gap-4'>
            <img
              src={feature.icon}
              alt=''
              className='h-16 w-16 flex-shrink-0 object-contain'
              loading='lazy'
            />
            <div className='flex-1'>
              <p className='text-gray-12 text-base font-semibold'>{feature.text}</p>
              {feature.note && (
                <p className='text-gray-12 text-[13px] font-normal leading-[140%]'>{feature.note}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
})

export default function PurchaseStepsSection({
  logClickedPurchaseStepsButtonAmazon,
  logClickedPurchaseStepsButtonRakuten,
  premiumFeaturesLayout,
  purchaseChannels,
}: {
  logClickedPurchaseStepsButtonAmazon: () => void
  logClickedPurchaseStepsButtonRakuten: () => void
  premiumFeaturesLayout: 'grid' | 'list'
  purchaseChannels: PurchaseChannelConfig[]
}) {
  const { t, i18n } = useTranslation('landingPage')
  const assets = landingAssetsForLocale(i18n.language)
  const steps = useMemo<Step[]>(() => {
    const premiumFeatures = t('purchaseSteps.premiumFeatures').split('\n')

    return [
      {
        stepNumber: 1,
        badgeText: t('purchaseSteps.steps.0.badge'),
        imageSrc: assets.stepImages[0],
        title: t('purchaseSteps.steps.0.title'),
        description: t('purchaseSteps.steps.0.description'),
      },
      {
        stepNumber: 2,
        badgeText: t('purchaseSteps.steps.1.badge'),
        imageSrc: assets.stepImages[1],
        title: <span className='whitespace-pre-line'>{t('purchaseSteps.steps.1.title')}</span>,
        description: t('purchaseSteps.steps.1.description'),
      },
      {
        stepNumber: 3,
        badgeText: t('purchaseSteps.steps.2.badge'),
        imageSrc: assets.stepImages[2],
        title: <span className='whitespace-pre-line'>{t('purchaseSteps.steps.2.title')}</span>,
        description: t('purchaseSteps.steps.2.description'),
        premiumFeatures: [
          {
            icon: assets.premiumFeatureIcons[0],
            text: premiumFeatures[0],
          },
          {
            icon: assets.premiumFeatureIcons[2],
            text: premiumFeatures[2],
          },
          {
            icon: assets.premiumFeatureIcons[1],
            text: premiumFeatures[1],
            note: t('purchaseSteps.premiumFeaturesNote'),
          },
        ],
      },
    ]
  }, [assets, t])
  const stepButtons = useMemo(
    () =>
      buildPurchaseChannelDisplayStates(purchaseChannels)
        .map(button => ({
          ...button,
          label:
            button.channel === 'amazon'
              ? t('purchaseButtons.amazonStoreLabel')
              : button.label,
          onClick:
            button.channel === 'amazon'
              ? logClickedPurchaseStepsButtonAmazon
              : logClickedPurchaseStepsButtonRakuten,
        }))
        .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary)),
    [purchaseChannels, logClickedPurchaseStepsButtonAmazon, logClickedPurchaseStepsButtonRakuten, t],
  )
  const isAmazonSaleActive = purchaseChannels.some(
    channel => channel.key === 'amazon' && channel.available && channel.saleActive,
  )

  return (
    <section className='space-y-6 px-4 py-8 pb-10'>
      <div className='text-center'>
        <h2 className='text-gray-12 whitespace-pre-line text-2xl font-semibold'>
          {t('purchaseSteps.heading')}
        </h2>
      </div>
      <div className='space-y-10'>
        {steps.map(step => (
          <div key={step.stepNumber}>
            <img
              src={step.imageSrc}
              alt={step.badgeText}
              className='mb-6 w-full rounded-lg'
              loading='lazy'
            />
            <StepBadge text={step.badgeText} />
            <div className='mt-2 space-y-4'>
              <h3 className='text-gray-12 tracking-0 text-center text-xl font-semibold leading-[140%]'>
                {step.title}
              </h3>
              <p className='localized-body-copy text-gray-12 tracking-0 text-base font-normal leading-[160%]'>
                {step.description}
              </p>

              {step.stepNumber === 1 && isAmazonSaleActive && (
                <p className='text-center text-base font-semibold leading-[25px] tracking-[-0.41px] text-mi-on-surface-accent-secondary'>
                  {t('purchaseSteps.amazonSaleText')}
                </p>
              )}

              {step.stepNumber === 1 && <StepButtons buttons={stepButtons} />}
              {step.premiumFeatures && (
                <PremiumFeaturesBox
                  features={step.premiumFeatures}
                  title={t('purchaseSteps.premiumFeaturesTitle')}
                  layout={premiumFeaturesLayout}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
